import { useState, useEffect } from "react";
import { toast } from "sonner";

const DECISIONS = [
  { id: "approve",                       label: "Approve",                                accent: "#2FB999", tone: "Grant full platform access", needs: null },
  { id: "approve_with_conditions",       label: "Approve with conditions",                accent: "#D8B23E", tone: "Access unlocks once partner acknowledges terms", needs: "conditions" },
  { id: "reject",                        label: "Reject",                                 accent: "#8A8A82", tone: "Silent — no email if reason blank", needs: null },
  { id: "reject_with_reason",            label: "Reject with reason",                     accent: "#C0392B", tone: "Sends a decision note to partner", needs: "reason" },
  { id: "reset_for_reregistration",      label: "Reject & refer for RE-REGISTRATION",     accent: "#B87333", tone: "Wipes KYC + eSign, forces the whole flow again", needs: "reason" },
  { id: "restore_access",                label: "Restore Access (revoked accounts)",      accent: "#2FB999", tone: "Undoes a cancellation — appealable path (veto only); 15-min deadline is re-armed if fee is still unpaid", needs: "reason_optional", only_cancelled: true },
];

/**
 * Unified decision modal for any Buyer / Seller.  Supports the 5-way choice
 * PLUS a Veto toggle that unlocks when the target is already-active.
 */
export function DecisionModal({ user, open, onClose, onDone, api }) {
  const [choice, setChoice] = useState("approve");
  const [reason, setReason] = useState("");
  const [conditions, setConditions] = useState("");
  const [veto, setVeto] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Default veto ON when target is already active — it's the only way to
    // touch them once onboarded.  Same for cancelled (restore path).
    setVeto(user?.status === "active" || user?.status === "cancelled");
    // Default action per state.
    if (user?.status === "cancelled") {
      setChoice("restore_access");
    } else {
      setChoice("approve");
    }
    setReason("");
    setConditions("");
  }, [open, user]);

  if (!open || !user) return null;

  // Filter the visible options — restore_access only appears for cancelled
  // users; the standard 5 don't apply to a revoked account.
  const visibleDecisions = DECISIONS.filter((d) =>
    user.status === "cancelled" ? d.id === "restore_access" : !d.only_cancelled);

  const meta = visibleDecisions.find(d => d.id === choice) || DECISIONS.find(d => d.id === choice);
  const canVeto = user.status === "active" || user.status === "cancelled";
  // restore_access ALWAYS runs on the veto endpoint.
  const forceVeto =
    (canVeto && ["reject", "reject_with_reason", "reset_for_reregistration"].includes(choice))
    || choice === "restore_access";
  const effectiveVeto = veto || forceVeto;

  const submit = async () => {
    if (meta?.needs === "reason" && !reason.trim()) {
      toast.error("Please provide a reason.");
      return;
    }
    if (meta?.needs === "conditions" && !conditions.trim()) {
      toast.error("Please list the conditions.");
      return;
    }
    const body = {
      decision: choice,
      reason: reason.trim() || undefined,
      conditions: conditions.trim() || undefined,
    };
    setBusy(true);
    try {
      const endpoint = effectiveVeto
        ? `/admin/users/${user.id}/veto`
        : `/admin/users/${user.id}/decision`;
      const { data } = await api.post(endpoint, body);
      toast.success(
        `${meta.label} · ${data.new_status}${effectiveVeto ? " (via veto)" : ""}${data.notified ? " · email sent" : ""}`
      );
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4" data-testid="decision-modal">
      <div className="w-full max-w-2xl border border-[#B87333]/40 bg-[#0a0a0a] rounded-sm shadow-2xl">
        <div className="p-6 md:p-8 border-b border-white/10 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="overline text-[#B87333]">Admin decision</div>
            <div className="font-serif text-2xl md:text-3xl mt-1 truncate">
              {user.business_name || user.name || user.email}
            </div>
            <div className="font-body text-sm text-[#8A8A82] mt-1">
              {user.email} · {user.account_type}{user.buyer_category ? ` · ${user.buyer_category}` : ""}
              {user.status ? ` · currently ${user.status}` : ""}
            </div>
          </div>
          <button data-testid="decision-modal-close" onClick={onClose}
                  className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] hover:text-white">
            Close
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="space-y-2">
            {visibleDecisions.map((d) => (
              <label key={d.id} data-testid={`decision-option-${d.id}`}
                     className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${choice === d.id ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"}`}
                     style={{ borderColor: choice === d.id ? `${d.accent}` : "rgba(255,255,255,0.10)" }}>
                <input type="radio" name="decision" value={d.id}
                       checked={choice === d.id} onChange={() => setChoice(d.id)}
                       className="mt-1 accent-[#B87333]" />
                <div>
                  <div className="font-grotesk text-sm uppercase tracking-[0.16em]" style={{ color: d.accent }}>{d.label}</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">{d.tone}</div>
                </div>
              </label>
            ))}
          </div>

          {meta?.needs === "conditions" && (
            <div>
              <div className="overline text-[#D8B23E] mb-2">Conditions (required · partner must acknowledge to trade)</div>
              <textarea data-testid="decision-conditions"
                        value={conditions} onChange={(e) => setConditions(e.target.value)}
                        rows={5}
                        placeholder="e.g. Weekly reporting on SS commitments. 5% cap on commissions. Quarterly review clause."
                        className="w-full bg-black/40 border border-[#D8B23E]/40 focus:border-[#D8B23E] outline-none rounded-sm p-3 font-body text-sm text-[#F5F5F0]" />
            </div>
          )}

          {(meta?.needs === "reason" || choice === "reject" || meta?.needs === "reason_optional") && (
            <div>
              <div className={`overline mb-2 ${meta?.needs === "reason" ? "text-[#C0392B]" : "text-[#8A8A82]"}`}>
                {meta?.needs === "reason"
                  ? "Reason (required)"
                  : meta?.needs === "reason_optional"
                    ? "Justification for restore (optional — stamped in audit log)"
                    : "Reason (optional — leave blank to revoke silently)"}
              </div>
              <textarea data-testid="decision-reason"
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        placeholder={choice === "restore_access"
                          ? "e.g. Buyer disputed timing — Razorpay callback logged 2 min after deadline"
                          : "Short note that will accompany the decision email"}
                        className="w-full bg-black/40 border border-white/20 focus:border-[#C0392B] outline-none rounded-sm p-3 font-body text-sm text-[#F5F5F0]" />
            </div>
          )}

          {canVeto && (
            <div className="p-4 rounded-sm border border-[#C0392B]/40 bg-[#C0392B]/[0.06]">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="decision-veto-toggle">
                <input type="checkbox" checked={effectiveVeto}
                       disabled={forceVeto}
                       onChange={(e) => setVeto(e.target.checked)}
                       className="mt-1 accent-[#C0392B]" />
                <div>
                  <div className="font-grotesk text-sm uppercase tracking-[0.16em] text-[#E08A7E]">
                    Admin VETO — override onboarding guard
                  </div>
                  <div className="font-body text-xs text-[#C9C9C2] mt-1 leading-relaxed">
                    {user.status === "cancelled"
                      ? "This account was auto-revoked. Restoring it is an appeal path and requires veto — every restoration is stamped in the audit log."
                      : "This partner has already completed eKYC and been onboarded. VETO lets you reject or reset them anyway. Every veto is stamped into the audit log for compliance."}
                    {forceVeto && <span className="block text-[#E08A7E] mt-1">Auto-enabled: the chosen decision requires veto.</span>}
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>

        <div className="p-6 md:p-8 border-t border-white/10 flex flex-wrap gap-3 justify-end">
          <button data-testid="decision-cancel" onClick={onClose}
                  className="rounded-full border border-white/20 px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-white/40 transition-colors">
            Cancel
          </button>
          <button data-testid="decision-submit" onClick={submit} disabled={busy}
                  className="rounded-full px-6 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] text-black hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: meta?.accent || "#B87333" }}>
            {busy ? "Applying…" : effectiveVeto ? `${meta?.label} (Veto)` : meta?.label}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Slide-in drawer showing every past decision on this user. */
export function DecisionLogDrawer({ user, open, onClose, api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    api.get(`/admin/users/${user.id}/decisions`)
      .then((r) => setItems(r.data.decisions || []))
      .catch(() => toast.error("Could not load decision log."))
      .finally(() => setLoading(false));
  }, [open, user, api]);

  if (!open || !user) return null;
  const tone = (a) => ({
    approve: "#2FB999",
    approve_with_conditions: "#D8B23E",
    reject: "#8A8A82",
    reject_with_reason: "#C0392B",
    reset_for_reregistration: "#B87333",
  })[a] || "#B87333";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" data-testid="decision-log-drawer" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="absolute right-0 top-0 h-full w-full max-w-lg border-l border-[#B87333]/40 bg-[#0a0a0a] p-6 md:p-8 overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="overline text-[#B87333]">Decision audit log</div>
            <div className="font-serif text-2xl mt-1">{user.business_name || user.email}</div>
          </div>
          <button data-testid="decision-log-close" onClick={onClose}
                  className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] hover:text-white">Close</button>
        </div>
        {loading ? <p className="font-body text-[#8A8A82]">Loading…</p> :
          items.length === 0 ? <p className="font-body text-[#8A8A82]" data-testid="decision-log-empty">No decisions on record yet.</p> :
          <ol className="space-y-4">
            {items.slice().reverse().map((d) => (
              <li key={d.id} className="border border-white/10 rounded-sm p-4" data-testid={`decision-log-row-${d.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm border" style={{ color: tone(d.action), borderColor: `${tone(d.action)}66` }}>
                    {d.action.replace(/_/g, " ")}
                  </span>
                  {d.veto && <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-[#C0392B] text-white">VETO</span>}
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82]">
                    {d.from_status} → {d.to_status}
                  </span>
                </div>
                {d.reason && <div className="font-body text-sm text-[#C9C9C2] mt-2 italic border-l-2 pl-3 py-1" style={{ borderColor: tone(d.action) }}>{d.reason}</div>}
                {d.conditions && <div className="font-body text-sm text-[#F5F5F0] mt-2 whitespace-pre-line border-l-2 pl-3 py-1 border-[#D8B23E]">{d.conditions}</div>}
                <div className="font-body text-xs text-[#8A8A82] mt-2">
                  by {d.by_admin_email || "admin"} · {new Date(d.at).toLocaleString()}
                </div>
              </li>
            ))}
          </ol>}
      </div>
    </div>
  );
}
